"use client";

/**
 * BottomCard — 3-variant bottom action card per §5.3
 *
 * V1 (votes < 15): "امضاهای کافی..." + DISABLED grey "اجرا کردن اقدام"
 * V2 (≥15, not executed, member only): "این اقدام امضاهای کافی..." + ACTIVE accent button → MC_Confirm → useExecuteProposal
 * V3 (executed): "این اقدام توسط کاربر زیر اجرا شده است!" + ExecutorRow
 *
 * L12: Non-member on V2 → null (hidden entirely).
 */

import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useExecuteProposal } from "@/hooks/dao/useExecuteProposal";
import { MC_Confirm } from "../shared/MC_Confirm";
import { ExecutorRow } from "./ExecutorRow";

type BottomCardVariant = "v1" | "v2" | "v3";

interface BottomCardV3Props {
  executorAddress: string;
  executorNickname: string | null;
}

interface BottomCardProps {
  variant: BottomCardVariant;
  isMember: boolean;
  txIndex: number;
  executor?: BottomCardV3Props | null;
}

export function BottomCard({
  variant,
  isMember,
  txIndex,
  executor,
}: BottomCardProps) {
  const t = useTranslations("dao");
  const [confirmOpen, setConfirmOpen] = useState(false);
  // `isExecuting` covers the FULL round-trip (chain submit → backend POST →
  // query invalidation → router.push). `isPending` from useExecuteProposal
  // only covers the chain submit half — relying on it alone makes the
  // spinner blink off during the backend leg, which looks like the click
  // "did nothing". Track our own state in the button's local closure.
  const [isExecuting, setIsExecuting] = useState(false);
  const { mutate, retryBackendRecord, hasBackendRecordFailure, isPending } =
    useExecuteProposal();
  const isBusy = isExecuting || isPending;

  if (variant === "v2" && !isMember) {
    return null;
  }

  return (
    <div className="rounded-xl border border-stroke-light bg-bg-secondary p-5 flex flex-col gap-4">
      {variant === "v1" && (
        <>
          <p className="text-sm text-text-primary">
            {t("proposals.detail.bottomCard.v1NotEnough")}
          </p>
          <Button disabled variant="bordered" className="w-full">
            {t("proposals.detail.bottomCard.executeAct")}
          </Button>
        </>
      )}

      {variant === "v2" && isMember && (
        <>
          <p className="text-sm text-text-primary">
            {t("proposals.detail.bottomCard.v2Ready")}
          </p>
          <Button
            onClick={() => setConfirmOpen(true)}
            loading={isBusy}
            loadingText={t("proposals.detail.bottomCard.executing")}
            variant="solid"
            colors="accent"
            className="w-full"
          >
            {t("proposals.detail.bottomCard.executeAct")}
          </Button>
          {hasBackendRecordFailure && (
            <Button
              type="button"
              variant="ghost"
              colors="default"
              className="w-full"
              onClick={() => retryBackendRecord(txIndex)}
            >
              {t("proposals.detail.bottomCard.retryRecord")}
            </Button>
          )}
          <MC_Confirm
            open={confirmOpen}
            title={t("proposals.detail.bottomCard.executeConfirmTitle")}
            body={t("proposals.detail.bottomCard.executeConfirmBody")}
            confirmLabel={t("proposals.detail.bottomCard.executeConfirmLabel")}
            confirmColors="secondary"
            confirmLoading={isBusy}
            onConfirm={async () => {
              setIsExecuting(true);
              try {
                await mutate(txIndex);
              } finally {
                // Close the modal AFTER the mutation resolves so the user
                // sees the spinner the whole time. Success path navigates
                // away (router.push) and unmounts before this runs; error
                // path stays here, hides spinner, modal closes.
                setIsExecuting(false);
                setConfirmOpen(false);
              }
            }}
            onCancel={() => {
              if (!isBusy) setConfirmOpen(false);
            }}
          />
        </>
      )}

      {variant === "v3" &&
        (executor ? (
          <>
            <p className="text-sm text-text-primary">
              {t("proposals.detail.bottomCard.v3Executed")}
            </p>
            <ExecutorRow
              address={executor.executorAddress}
              nickname={executor.executorNickname}
            />
          </>
        ) : (
          // Chain says executed but backend has no executor row (seeded
          // fork, or POST /dao/:index/execute failed post-chain). Without
          // this fallback the whole V3 block returns null → empty card.
          <p className="text-sm text-text-primary">
            {t("proposals.detail.bottomCard.v3ExecutedNoExecutor")}
          </p>
        ))}
    </div>
  );
}
